// lineApi — typed CRUD for /v1/line/groups (Route 12).
import { apiFetch } from "../../lib/api-client.js";

export type LineGroupStatus = "active" | "disabled";

export interface LineGroupRow {
  id: string;
  tenant_id: string;
  channel_id: string;
  binding_kind: string | null;
  group_id: string | null;
  display_name: string | null;
  status: LineGroupStatus | string;
  created_at: string;
  rules: string[];
  last_delivery: string | null;
}

export interface LineGroupListResponse { items: LineGroupRow[]; correlation_id: string | null }

export class LineApiError extends Error {
  status: number; code: string; correlationId: string | null; upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, upstreamMessage?: string) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.upstreamMessage = upstreamMessage;
  }
}
export function friendlyLineError(err: unknown): string {
  if (err instanceof LineApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能修改 LINE 通知群組。";
    if (err.status === 404) return "找不到此 LINE 群組綁定。";
    if (err.status === 400 && err.code === "tenant_id_required") return "尚未選定操作的單位。";
    const msgPart = err.upstreamMessage ? `・msg=${err.upstreamMessage}` : "";
    return `LINE 群組資料暫時無法存取（HTTP ${err.status}・code=${err.code}${msgPart}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}
async function asError(r: Response): Promise<LineApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let msg: string | undefined;
  try { const b = (await r.json()) as { error?: string; correlation_id?: string; message?: string }; code = b.error ?? code; cid = b.correlation_id ?? cid; msg = b.message; } catch {}
  return new LineApiError(r.status, code, cid, msg);
}

export async function listLineGroups(tenantId?: string | null, status?: LineGroupStatus | null): Promise<LineGroupListResponse> {
  const qs = new URLSearchParams();
  if (tenantId) qs.set("tenant_id", tenantId);
  if (status) qs.set("status", status);
  const r = await apiFetch(`/v1/line/groups${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as LineGroupListResponse;
}

export async function patchLineGroup(id: string, body: { status?: LineGroupStatus; display_name?: string }, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/line/groups/${encodeURIComponent(id)}${qs}`, {
    method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(body), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}

export async function disableLineGroup(id: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/line/groups/${encodeURIComponent(id)}${qs}`, { method: "DELETE", credentials: "include" });
  if (!r.ok) throw await asError(r);
}

/** Pending group bindings — rows the webhook landed on the platform
 *  sentinel because it couldn't auto-resolve the inviter's tenant.
 *  The current tenant can `claimLineGroup` them. */
export interface PendingLineGroupRow {
  id: string;
  tenant_id: string;          // 一定是 `_line_channel_<id>` 哨兵
  channel_id: string;
  binding_kind: string | null;
  group_id: string | null;
  display_name: string | null;
  status: string;
  created_at: string;
}
export interface PendingLineGroupResponse { items: PendingLineGroupRow[]; correlation_id: string | null }

export async function listPendingLineGroups(tenantId?: string | null): Promise<PendingLineGroupResponse> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/line/groups/pending${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as PendingLineGroupResponse;
}

export async function claimLineGroup(id: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/line/groups/${encodeURIComponent(id)}/claim${qs}`, {
    method: "POST", credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}
